import { useCallback, useEffect, useMemo } from 'react';

import { PagePagination } from '@/components/pagination';
import { ProtectedComponent } from '@/components/ProtectedComponent';
import { DataTable } from '@/components/ui/data-table';

import { useEventHandler } from '@/hooks/use-event-handler';
import { useToggle } from '@/hooks/use-toggle';

import { useApiKeysQuery } from '@/query/api-key.query';

import { DEFAULT_PAGE_SIZE, Permissions } from '@/constants';

import { ApiKeysCreatePage } from '../ApiKeysCreate/ApiKeysCreate';

import { getColumns } from './components/columns';
import { ApiKeyEmptyResult } from './components/empty';

import { useApiKeysListStore } from '@/store/api-keys-list.store';
import type { ApiKey } from '@/types/api-key.type';
import { type ColumnDef } from '@tanstack/react-table';

export default function ApiKeys() {
  const { state, toggle, open } = useToggle();

  const { currentPage, pageSize, query, setCurrentPage, setPageSize, setQuery, sort, setSort } =
    useApiKeysListStore();

  const { data, isLoading } = useApiKeysQuery({
    page: currentPage,
    page_size: pageSize,
    query,
  });

  useEventHandler({
    name: 'key:create',
    eventHandler: open,
  });

  const { reset } = useApiKeysListStore();

  useEffect(() => {
    return () => {
      reset();
    };
  }, []);

  const handleClearQuery = useCallback(() => setQuery(''), [setQuery]);
  const hasQuery = query && query.trim() !== '';

  const apiKeys: ApiKey[] = data?.data || [];
  const meta = data?.meta;

  const columns = useMemo<ColumnDef<ApiKey>[]>(() => getColumns(), []);

  return (
    <>
      <div className="flex flex-col h-full space-y-6">
        <div className="bg-background border rounded-lg">
          <DataTable
            columns={columns}
            data={apiKeys}
            sorting={sort}
            isLoading={isLoading}
            onSortingChange={setSort}
            emptyState={
              <ApiKeyEmptyResult hasQuery={hasQuery} handleClearQuery={handleClearQuery} />
            }
          />

          {/* PagePagination */}
          <PagePagination
            isLoading={isLoading}
            currentPage={meta?.page || 1}
            pageSize={meta?.page_size || DEFAULT_PAGE_SIZE}
            totalItems={meta?.total_items || 0}
            totalPages={meta?.total_pages || 1}
            setPageSize={size => {
              setPageSize(size);
              setCurrentPage(1); // Reset to first page when page size changes
            }}
            setCurrentPage={setCurrentPage}
          />
        </div>
      </div>

      {/* Create API Key Dialog */}
      <ProtectedComponent requiredPermissions={[Permissions.ApiKeyCreateUpdate]}>
        {state && <ApiKeysCreatePage toggle={toggle} />}
      </ProtectedComponent>
    </>
  );
}
